import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE;

export default function ForgotPasswordPage() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error(t('auth.forgot.enter_email', 'Please enter your email address'));
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
            setIsSubmitted(true);
        } catch (error) {
            // Always show success to prevent email enumeration
            setIsSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo / Brand */}
                <div className="text-center mb-8 flex flex-col items-center gap-2">
                    <img src="/owntune-logo-dark.png" alt="Owntune" className="h-8 w-auto" />
                    <p className="text-sm text-slate-500">{t('brand.tagline', 'Vinyl Order Management')}</p>
                </div>

                <Card className="shadow-premium-lg">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-xl">
                            {isSubmitted
                                ? t('auth.forgot.check_email_title', 'Check your email')
                                : t('auth.forgot.title', 'Forgot your password?')}
                        </CardTitle>
                        <CardDescription>
                            {isSubmitted
                                ? t('auth.forgot.check_email_desc', 'We sent you a password reset link')
                                : t('auth.forgot.subtitle', "Enter your email and we'll send you a reset link")
                            }
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        {isSubmitted ? (
                            <div className="space-y-6 py-4">
                                <div className="flex justify-center">
                                    <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <CheckCircle className="h-8 w-8 text-emerald-600" />
                                    </div>
                                </div>

                                <Alert className="bg-background border-border">
                                    <Mail className="h-4 w-4" />
                                    <AlertDescription>
                                        {t(
                                            'auth.forgot.email_sent_if_exists',
                                            'If an account with {email} exists, you will receive a password reset link shortly.',
                                            { email }
                                        )}
                                    </AlertDescription>
                                </Alert>

                                <div className="text-center text-sm text-slate-500">
                                    <p>{t('auth.forgot.no_email_hint', "Didn't receive the email? Check your spam folder or")}</p>
                                    <button
                                        onClick={() => setIsSubmitted(false)}
                                        className="text-purple-600 hover:text-purple-700 font-medium"
                                    >
                                        {t('auth.forgot.try_different_email', 'try again with a different email')}
                                    </button>
                                </div>

                                <div className="pt-4 border-t">
                                    <Link to="/login">
                                        <Button variant="outline" className="w-full">
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            {t('auth.back_to_signin', 'Back to Sign In')}
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.email_label', 'Email address')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder={t('auth.email_placeholder', 'you@example.com')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoFocus
                                        data-testid="forgot-email-input"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-slate-900 hover:bg-slate-800"
                                    disabled={isSubmitting}
                                    data-testid="send-reset-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('common.sending', 'Sending...')}
                                        </>
                                    ) : (
                                        t('auth.forgot.send_reset_link', 'Send Reset Link')
                                    )}
                                </Button>

                                <div className="text-center pt-2">
                                    <Link
                                        to="/login"
                                        className="text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                                    >
                                        <ArrowLeft className="h-3 w-3" />
                                        {t('auth.back_to_signin', 'Back to Sign In')}
                                    </Link>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
